import React, { useCallback, useEffect, useMemo } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiBox,
  MiBuildPageHeader,
  MiButton,
  MiIcon,
  MiIconButton,
  MiInputTextbox,
  MiList,
  MiStackedTypography,
  MiTab,
} from "@miplatform/components";
import { PAGINATION } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { ButtonVariants, IInvoiceResponse } from "@miplatform/types";
import { getNestedValue, parseFieldValue } from "@miplatform/utils";
import { mdiMagnify, mdiPlus, mdiPrinterOutline } from "@mdi/js";
import { Box, Divider, InputAdornment, Theme, Typography } from "@mui/material";
import { Filters as ARFilters, FilterComponent } from "./FilterComponent";
import {
  FIELD_WIDTH,
  Field,
  OPEN_INVOICES_TAB,
  PAID_INVOICES_TAB,
  TabValue,
  getListConfig,
  tabs,
} from "./constants";
import { useAccountsReceivableViewModel } from "./useAccountsReceivableViewModel";

export const AccountsReceivable: React.FC = () => {
  const { classes, theme } = useStyles();
  const { params, navigate } = useRouter();
  const { tab } = params;
  const {
    search,
    setSearch,
    isLoading,
    invoices,
    setInvoices,
    getInvoices,
    debouncedSearch,
    getTenant,
    handlePrint,
  } = useAccountsReceivableViewModel({ tab: tab as TabValue });

  const [filters, setFilters] = React.useState<ARFilters>({
    groupBy: "",
    sortBy: "",
    accountId: "",
    communityId: "",
  });

  const LIST_CONFIG = getListConfig(filters);

  useEffect(() => {
    getTenant();
  }, []);

  useEffect(() => {
    setFilters({
      groupBy: "",
      sortBy: "",
      accountId: "",
      communityId: "",
    });
    setSearch("");
  }, [tab]);

  const handleSearch = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(event.target.value);
  };

  const LIST_DATA_CONFIG = {
    data: invoices,
    setData: setInvoices,
    dataProvider: (params: any) => getInvoices({ ...params, filters }),
  };

  const handleTabChange = (selectedTab: string): void => {
    setSearch("");
    navigate(`/accounting/accounts-receivable/${selectedTab}`);
  };

  const renderTitle = (item: any) => {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Typography color="primary.main" fontWeight={600} fontSize={14}>
          {item.home?.address?.addressLine1 || " "}
        </Typography>
      </Box>
    );
  };

  const getPlaceHolderText = (): string => {
    switch (tab) {
      case OPEN_INVOICES_TAB:
        return "Search Open Invoices";
      case PAID_INVOICES_TAB:
        return "Search Paid Invoices";
      default:
        return "Search";
    }
  };

  const sections: any[] = [
    {
      fieldsetStyle: {
        justifyContent: "flex-end",
        alignItems: "center",
      },
      fieldset: [
        {
          field: "priority",
          maxWidth: 600,
          justifyContent: "flex-end",
          renderCell: (row: IInvoiceResponse) => {
            return (
              <Box
                position="relative"
                display="flex"
                flexWrap="wrap"
                justifyContent="flex-end"
                gap={theme.spacing(2)}
              >
                {tab &&
                  LIST_CONFIG[tab as TabValue]?.fields?.map((field: Field) => {
                    const { label, fieldKey, width, type } = field;

                    return (
                      <MiStackedTypography
                        key={fieldKey}
                        title={label}
                        titleProps={{ width: width || FIELD_WIDTH }}
                        description={parseFieldValue(
                          getNestedValue(row, fieldKey),
                          label.toLowerCase().includes("date")
                            ? "--/--/--"
                            : undefined,
                          undefined,
                          { isCurrency: type === "currency", currency: "USD" }
                        )}
                        descriptionProps={{
                          width: width || FIELD_WIDTH,
                          fontWeight: 600,
                        }}
                      />
                    );
                  })}
              </Box>
            );
          },
        },
      ],
    },
  ];

  const createMiList = useCallback(
    (isPrint = false) => (
      <MiList
        showGroupCount
        groupedMode={!!LIST_CONFIG[tab as TabValue]?.getGroupedCriteria}
        viewType="card"
        loading={isPrint ? false : isLoading}
        cardProps={{
          sections,
          renderTitle,
        }}
        groupedCriteria={
          tab ? LIST_CONFIG[tab as TabValue]?.getGroupedCriteria : undefined
        }
        data={LIST_DATA_CONFIG.data}
        setData={LIST_DATA_CONFIG.setData}
        dataProvider={LIST_DATA_CONFIG.dataProvider}
        refresh={filters}
        search={debouncedSearch}
        paginationMode={PAGINATION.SERVER}
        hideListPagination={isPrint}
        defaultPageSize={25}
        groupedTitle={
          tab ? LIST_CONFIG[tab as TabValue]?.getGroupedTitle : undefined
        }
        emptyListProps={{
          text: "No Invoices found",
        }}
      />
    ),
    [LIST_DATA_CONFIG, isLoading, filters, debouncedSearch]
  );

  const displayList = useMemo(() => createMiList(false), [createMiList]);
  const printList = useMemo(() => createMiList(true), [createMiList]);

  const handlePrintClick = useCallback(() => {
    handlePrint(printList);
  }, [handlePrint, printList]);

  const canPrint = useMemo(
    () => !isLoading && invoices.length > 0,
    [isLoading, invoices.length]
  );
  return (
    <>
      <MiBuildPageHeader
        title="Accounts Receivable"
        loading={isLoading}
        actionComponent={
          <Box className={classes.actionContainer}>
            <MiButton
              title="Post Payment"
              variant={ButtonVariants.Contained}
              startIcon={{ path: mdiPlus }}
              onClick={() => navigate("/accounting/payments/new")}
            />
            <MiIconButton
              data-testid="mi-orders-print-icon-button"
              disabled={!canPrint}
              onClick={handlePrintClick}
              className={!canPrint ? classes.disabledIconButton : undefined}
            >
              <MiIcon path={mdiPrinterOutline} color="grey" />
            </MiIconButton>
          </Box>
        }
      />

      <MiTab tabs={tabs} currentTab={tab} onTabChange={handleTabChange} />

      <MiBox
        sx={{
          display: "flex",
          flexDirection: "row",
          flex: 1,
          height: "100%",
          overflow: "hidden",
          padding: 4,
          gap: 4,
        }}
      >
        <Box
          height="100%"
          flex={0.8}
          sx={{ overflow: "auto", display: "flex", flexDirection: "column" }}
        >
          <Box
            display="flex"
            alignItems="center"
            mb={2}
            p={2}
            justifyContent="space-between"
          >
            <Typography variant="h5" fontWeight={600} className={classes.title}>
              {tabs.find((tabItem) => tabItem.value === tab)?.label || ""}
            </Typography>
            <MiInputTextbox
              value={search}
              style={{
                maxWidth: 220,
                marginLeft: theme.spacing(2),
                backgroundColor: theme.palette.common.white,
              }}
              handleChange={handleSearch}
              placeholder={getPlaceHolderText()}
              textfieldProps={{
                style: {
                  fontSize:
                    (theme as unknown as { fontSize: { medium: number } })
                      .fontSize?.medium || 14,
                  height: 34,
                },
                startAdornment: (
                  <InputAdornment position="start">
                    <MiIcon path={mdiMagnify} size={0.8} color="grey" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Box sx={{ flex: 1, overflow: "auto" }}>{displayList}</Box>
        </Box>
        <Divider orientation="vertical" flexItem />

        <FilterComponent filters={filters} setFilters={setFilters} />
      </MiBox>
    </>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  actionContainer: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
  },
  title: {
    color: theme.palette.text.primary,
    paddingLeft: theme.spacing(1),
  },
  disabledIconButton: {
    opacity: 0.4,
    pointerEvents: "none",
  },
}));
